import {useCallback, useRef} from 'react';

import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import {useTranslation} from 'react-i18next';

import ChangeCardThemeModal from '@/app/main/onboarding/components/ChangeCardThemeModal/ChangeCardThemeModal';
import FamilyCodeBanner from '@/app/main/onboarding/components/FamilyCodeBanner/FamilyCodeBanner';
import PrivacyPolicy from '@/app/main/onboarding/components/PrivacyPolicy/PrivacyPolicy';
import {AppButton} from '@/app/shared-components';
import ChangeTextWithColorAndImage from '@/app/shared-components/ColorAndImageText/ColorAndImageText';
import InputTextField from '@/app/shared-components/InputTextField/InputTextField';
import SecureEncryptFooter from '@/app/shared-components/SecureEncryptFooter/SecureEncryptFooter';
import {COLORS} from '@/styles/colors';
import {validateNameInput} from '@/utils/validateNames';

import {useViewModelProvider, ViewModelProvider} from './ViewModelContext';

import './styles';

const months = [
  {value: '01', label: 'January'},
  {value: '02', label: 'February'},
  {value: '03', label: 'March'},
  {value: '04', label: 'April'},
  {value: '05', label: 'May'},
  {value: '06', label: 'June'},
  {value: '07', label: 'July'},
  {value: '08', label: 'August'},
  {value: '09', label: 'September'},
  {value: '10', label: 'October'},
  {value: '11', label: 'November'},
  {value: '12', label: 'December'},
];

const invalidKeys = new Set(['ArrowUp', 'ArrowDown', '-', ',', '.']);

const PersonalInfoContent = () => {
  const {t} = useTranslation();
  const {
    selectedTheme,
    firstName,
    lastName,
    day,
    month,
    year,
    isDisabled,
    isSplitDateValid,
    isModalOpen,
    setFirstName,
    setLastName,
    setDay,
    setMonth,
    setYear,
    handleContinue,
    onThemeSelected,
    handleOpenModal,
    handleCloseModal,
    handlerDayAndMontOnBlur,
    showFamilyCodeBanner,
    handleKidLinked,
  } = useViewModelProvider();

  const handleKeyDownNumber = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (invalidKeys.has(event.key)) {
        event.preventDefault();
      }
    },
    [],
  );

  const inputRefDay = useRef<HTMLInputElement>(null);
  const inputRefYear = useRef<HTMLInputElement>(null);

  return (
    <Box className="personal-info-container">
      <Box
        className="personal-info-content"
        sx={{
          overflowY: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}>
        <Box className="personal-info-title-container">
          <ChangeTextWithColorAndImage
            text={
              firstName
                ? t('add_kid.personal_info.title_with_name', {
                    name: firstName,
                  })
                : t('add_kid.personal_info.title')
            }
            textStyle={{
              color: COLORS.ON_SURFACE,
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: -0.22,
            }}
          />
          <Typography className="personal-info-subtitle">
            {t('add_kid.personal_info.subtitle')}
          </Typography>
        </Box>
        {showFamilyCodeBanner && (
          <FamilyCodeBanner
            kidName={firstName}
            onKidLinked={handleKidLinked}
            isAddKidLastStep={false}
          />
        )}
        <Box
          className="w-full flex"
          sx={{gap: 3}}>
          <Box className="w-full">
            <InputTextField
              id="add-kid-personal-info-first-name-input"
              label={t('add_kid.personal_info.first_name')}
              value={firstName}
              setValue={(value: string) =>
                setFirstName(validateNameInput(value))
              }
              onBlur={() => setFirstName(firstName.trim())}
              maxLength={20}
            />
          </Box>
          <Box className="w-full">
            <InputTextField
              id="add-kid-personal-info-last-name-input"
              label={t('add_kid.personal_info.last_name')}
              value={lastName}
              setValue={(value: string) =>
                setLastName(validateNameInput(value))
              }
              onBlur={() => setLastName(lastName.trim())}
              maxLength={20}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
          }}>
          <FormControl
            fullWidth
            variant="outlined"
            sx={{
              '& .MuiInputLabel-root': {
                fontSize: '16px',
                transform: 'translate(16px, 16px) scale(1)',
                '&.MuiInputLabel-shrink': {
                  fontSize: '14px',
                  transform: 'translate(10px, -7px) scale(0.75)',
                  backgroundColor: 'white',
                  padding: '0 4px',
                  color: COLORS.ON_SURFACE_1,
                },
                '&.Mui-error': {
                  color: COLORS.RED_CINNABAR,
                },
              },

              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                height: '56px',
                alignItems: 'center',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isSplitDateValid
                    ? COLORS.ON_OUTLINE_LIGHT
                    : COLORS.RED_CINNABAR,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: isSplitDateValid
                    ? COLORS.ON_SURFACE_1
                    : COLORS.RED_CINNABAR,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: isSplitDateValid
                    ? COLORS.ON_SURFACE_1
                    : COLORS.RED_CINNABAR,
                },
              },
            }}
            error={!isSplitDateValid}>
            <InputLabel htmlFor="month">
              {t('add_kid.personal_info.month')}
            </InputLabel>
            <Select
              className="text-left"
              id="add-kid-personal-info-month-select"
              value={month}
              onChange={e => setMonth(e.target.value)}
              onBlur={handlerDayAndMontOnBlur}
              displayEmpty
              sx={{
                '& .MuiSelect-select': {
                  padding: '16px',
                  fontSize: '16px',
                  fontWeight: 400,
                  lineHeight: '20px',
                  color: COLORS.ON_SURFACE,
                },
                '& .MuiSelect-icon': {
                  color: COLORS.ON_SURFACE_1,
                },
              }}>
              {months.map(m => (
                <MenuItem
                  key={m.value}
                  value={m.value}
                  sx={{
                    fontSize: '14px',
                    fontWeight: '400',
                    color: COLORS.ON_SURFACE_VARIANT,
                    '&.Mui-selected': {
                      backgroundColor: COLORS.ON_SURFACE_4,
                    },
                  }}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <InputTextField
            id="add-kid-personal-info-day-input"
            label={t('add_kid.personal_info.day')}
            type="number"
            value={day}
            setValue={setDay}
            onBlur={handlerDayAndMontOnBlur}
            maxLength={2}
            error={!isSplitDateValid}
            onKeyDown={handleKeyDownNumber}
            min={1}
            inputRef={inputRefDay}
          />

          <InputTextField
            id="add-kid-personal-info-year-input"
            label={t('add_kid.personal_info.year')}
            type="number"
            value={year}
            setValue={setYear}
            maxLength={4}
            error={!isSplitDateValid}
            onKeyDown={handleKeyDownNumber}
            min={2000}
            inputRef={inputRefYear}
          />
        </Box>
        {!isSplitDateValid && (
          <Typography
            marginTop={-2}
            marginLeft={1}
            sx={{
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: 12,
              letterSpacing: 0.25,
              color: COLORS.LIGHT_INVALID,
            }}>
            {t('add_kid.personal_info.invalid_date')}
          </Typography>
        )}
        <Box style={{display: 'flex', flexDirection: 'column'}}>
          <Box
            className="card-theme-container"
            sx={{
              position: 'relative',
              width: '240px',
              maxWidth: '300px',
              margin: 'auto',
            }}>
            <Box
              component="img"
              src={`/assets/images/cards/MoCardFront-${selectedTheme}.webp`}
              alt="Kid's card"
              sx={{
                width: '240px',
                maxWidth: '300px',
                margin: 'auto',
              }}
            />
            <Typography
              sx={{
                position: 'absolute',
                color: 'white',
                marginTop: '-30px',
                marginLeft: '13px',
                textShadow: '0px 0.513px 0.513px rgba(0, 0, 0, 0.25)',
                fontFamily: 'Roboto',
                fontSize: 13.333,
                fontStyle: 'normal',
                fontWeight: 700,
                maxWidth: 160,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
              {firstName.split(' ')[0]} {lastName.split(' ')[0]}
            </Typography>
            <Box className="card-theme-action-container">
              <Button
                className="card-theme-action-title"
                onClick={handleOpenModal}>
                {t('add_kid.personal_info.change_design')}
              </Button>
              {/* Change card theme modal */}
              <ChangeCardThemeModal
                open={isModalOpen}
                onClose={handleCloseModal}
                onCardThemeSelected={onThemeSelected}
              />
            </Box>
          </Box>
          <SecureEncryptFooter />
          <Box style={{marginLeft: '4px'}}>
            <PrivacyPolicy />
          </Box>
        </Box>
        <AppButton
          id="add-kid-personal-info-continue-button"
          disabled={isDisabled}
          label={t('add_kid.personal_info.continue')}
          onClick={handleContinue}
          mobileStickyStyle
        />
      </Box>
    </Box>
  );
};

const PersonalInfoPage = () => {
  return (
    <ViewModelProvider>
      <PersonalInfoContent />
    </ViewModelProvider>
  );
};

export default PersonalInfoPage;
